import React, { useState, useEffect, useMemo } from 'react';
import { 
  Typography, Grid, Paper, Box, Card, CardContent, 
  CardHeader, Divider, CircularProgress, Table,
  TableBody, TableCell, TableContainer, TableHead,
  TableRow, Chip, Tooltip, IconButton, tableCellClasses,
  ButtonGroup, Button, TablePagination, styled
} from '@mui/material';
import { 
  Dashboard as DashboardIcon,
  Tv as TvIcon,
  Campaign as CampaignIcon,
  VideoLibrary as VideoLibraryIcon,
  TrendingUp as TrendingUpIcon,
  DateRange as DateRangeIcon,
  AccessTime as AccessTimeIcon,
  BarChart as BarChartIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { format, parseISO, isValid, subDays, getHours } from 'date-fns';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, 
  Legend, ResponsiveContainer, BarChart, Bar, LabelList
} from 'recharts';
import { ptBR } from 'date-fns/locale';
import SidebarLayout from '../../components/layout/SidebarLayout';
import { useAuth } from '../../context/AuthContext';
import RegistrosService, { Registro, RegistroFilter as BaseRegistroFilter } from '../../services/registrosService';
import AcessosService, { UserAcessos } from '../../services/acessosService';
import EmissorasService from '../../services/emissorasService';
import MarcasService from '../../services/marcasService';

// Extending the base RegistroFilter to include the emissora_ids field
interface RegistroFilter extends BaseRegistroFilter {
  emissora_ids?: number[];
}

// Styled table cells for better visualization
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
  transition: 'background-color 0.2s',
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
  },
}));

type DateRange = 7 | 14 | 30;

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [dateRange, setDateRange] = useState<DateRange>(14); // Default to 14 days
  const [registros, setRegistros] = useState<Registro[]>([]);
  // Listas de nomes (a API só devolve emissora_id/marca_id, não os objetos aninhados)
  const [emissorasDisponiveis, setEmissorasDisponiveis] = useState<Array<{ id: number; nome: string }>>([]);
  const [marcasDisponiveis, setMarcasDisponiveis] = useState<Array<{ id: number; nome: string }>>([]);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage] = useState<number>(15);
  const [acessos, setAcessos] = useState<UserAcessos>({
    emissoras: [],
    marcas: [],
    itens: [],
    setores: []
  });
  const [registroStats, setRegistroStats] = useState<{
    total: number;
    emissorasCount: number;
    duracaoTotal: number;
    custoTotal: number;
    dailyData: {date: string; count: number}[];
    hourlyData: {hour: string; count: number}[];
  }>({ 
    total: 0, 
    emissorasCount: 0, 
    duracaoTotal: 0, 
    custoTotal: 0,
    dailyData: [],
    hourlyData: []
  });

  // Load user access permissions
  useEffect(() => {
    const loadUserAcessos = async () => {
      try {
        console.log('Buscando acessos do usuário para dashboard...');
        const dadosAcessos = await AcessosService.getUserAcessos();
        setAcessos(dadosAcessos);
        console.log('Acessos carregados:', dadosAcessos);
      } catch (error) {
        console.error('Erro ao carregar acessos do usuário:', error);
      }
    };

    loadUserAcessos();
  }, []);

  useEffect(() => {
    const loadRegistros = async () => {
      setIsLoading(true);
      try {
        // Get date range in the format expected by the API
        const today = new Date();
        const startDate = subDays(today, dateRange - 1);
        
        const formattedStartDate = format(startDate, 'yyyy-MM-dd');
        const formattedEndDate = format(today, 'yyyy-MM-dd');
        
        // Create filter to get records for the selected range
        let filter: RegistroFilter = {
          datahora_inicio: `${formattedStartDate}T00:00:00`,
          datahora_fim: `${formattedEndDate}T23:59:59`
        };
        
        // Apply user permission filters if not admin
        if (!user?.is_admin) {
          if (acessos.emissoras.length > 0) {
            // Create a filter with user permissions
            filter = {
              ...filter,
              emissora_ids: acessos.emissoras
            };
            console.log('Aplicando filtro de emissoras por permissão do usuário:', acessos.emissoras);
          } else {
            console.log('Usuário não tem permissões de emissoras configuradas');
          }
        } else {
          console.log('Usuário é admin, não aplicando filtros de permissão');
        }
        
        console.log('Buscando registros com filtros:', filter);
        
        // Fetch records with a higher limit to accommodate more days
        // Emissoras (com valor/tarifa) — carrega com await para já usar no custo abaixo
        let emissorasLista: any[] = emissorasDisponiveis;
        if (emissorasLista.length === 0) {
          emissorasLista = await EmissorasService.getEmissoras({}, 0, 2000).catch(() => []);
          setEmissorasDisponiveis(Array.isArray(emissorasLista) ? emissorasLista : []);
        }
        if (marcasDisponiveis.length === 0) {
          MarcasService.getMarcas({}, 0, 50000)
            .then((d: any) => setMarcasDisponiveis(Array.isArray(d) ? d : []))
            .catch(() => {});
        }

        const recordsData = await RegistrosService.getRegistros(filter, 0, 500);
        
        // Apply client-side filtering for permissions (similar to RelatoriosPage)
        const registrosFiltrados = recordsData.filter((registro: Registro) => {
          // If user is admin, they can see everything
          if (user?.is_admin) {
            return true;
          }
          
          // Check if user has access to all aspects of this record
          // Similar to the filtering in RelatoriosPage
          const temAcessoEmissora = !registro.emissora_id || acessos.emissoras.includes(registro.emissora_id);
          const temAcessoMarca = !registro.marca_id || acessos.marcas.includes(registro.marca_id);
          const temAcessoItem = !registro.item_id || acessos.itens.includes(registro.item_id);
          const temAcessoSetor = !registro.setor_id || acessos.setores.includes(registro.setor_id);
          
          // The record is only visible if the user has access to all its fields
          return temAcessoEmissora && temAcessoMarca && temAcessoItem && temAcessoSetor;
        });
        
        console.log(`Registros filtrados: ${registrosFiltrados.length} de ${recordsData.length} (filtrados por permissões de acesso)`);

        // Preenche o custo com a TARIFA DA EMISSORA (emissora.valor) quando o registro
        // não tem custo próprio — assim o valor aparece na coluna e no custo total.
        const valorPorEmissora: Record<number, number> = {};
        (Array.isArray(emissorasLista) ? emissorasLista : []).forEach((e: any) => { if (e && e.valor) valorPorEmissora[e.id] = e.valor; });
        registrosFiltrados.forEach((r: Registro) => {
          if ((!r.custo || r.custo === 0) && r.emissora_id && valorPorEmissora[r.emissora_id]) {
            r.custo = valorPorEmissora[r.emissora_id];
          }
        });

        setRegistros(registrosFiltrados);
        
        // Calculate statistics
        const emissorasSet = new Set(registrosFiltrados.map((r: Registro) => r.emissora_id));
        const duracaoTotal = registrosFiltrados.reduce((sum: number, r: Registro) => sum + (r.duracao || 0), 0);
        const custoTotal = registrosFiltrados.reduce((sum: number, r: Registro) => sum + (r.custo || 0), 0);
        
        // Generate daily data for chart visualization
        const dailyDataMap = new Map<string, number>();
        
        // Generate hourly data for time distribution chart
        const hourlyDataMap = new Map<number, number>();
        
        // Initialize hours (0-23)
        for (let hour = 0; hour < 24; hour++) {
          hourlyDataMap.set(hour, 0);
        }
        
        // Initialize all dates in the range
        for (let i = 0; i < dateRange; i++) {
          const date = format(subDays(today, i), 'yyyy-MM-dd');
          dailyDataMap.set(date, 0);
        }
        
        // Count records per day and hour
        registrosFiltrados.forEach((record: Registro) => {
          if (record.datahora) {
            // Process for daily data
            const date = record.datahora.split('T')[0];
            if (dailyDataMap.has(date)) {
              dailyDataMap.set(date, (dailyDataMap.get(date) || 0) + 1);
            }
            
            // Process for hourly data
            const recordDate = parseISO(record.datahora);
            if (isValid(recordDate)) {
              const hour = getHours(recordDate);
              hourlyDataMap.set(hour, (hourlyDataMap.get(hour) || 0) + 1);
            }
          }
        });
        
        // Convert maps to arrays for easier rendering
        const dailyData = Array.from(dailyDataMap.entries())
          .map(([date, count]) => ({
            date,
            count,
            formattedDate: format(parseISO(date), 'dd/MM')
          }))
          .sort((a, b) => a.date.localeCompare(b.date));
          
        const hourlyData = Array.from(hourlyDataMap.entries())
          .map(([hour, count]) => ({ 
            hour: hour.toString().padStart(2, '0') + ':00', 
            count 
          }))
          .sort((a, b) => a.hour.localeCompare(b.hour));
        
        setRegistroStats({
          total: registrosFiltrados.length,
          emissorasCount: emissorasSet.size,
          duracaoTotal,
          custoTotal,
          dailyData,
          hourlyData
        });
      } catch (error) {
        console.error('Error loading today\'s records:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadRegistros();
  }, [dateRange, acessos, user?.is_admin]); // Re-run when dateRange changes

  const statsCards = [
    {
      title: 'Emissoras',
      value: isLoading ? '-' : registroStats.emissorasCount.toString(),
      icon: <TvIcon fontSize="large" color="primary" />,
      description: 'Emissoras com registros'
    },
    {
      title: 'Total de Registros',
      value: isLoading ? '-' : registroStats.total.toString(),
      icon: <CampaignIcon fontSize="large" color="primary" />,
      description: `Últimos ${dateRange} dias`
    },
    {
      title: 'Duração Total',
      value: isLoading ? '-' : formatDuration(registroStats.duracaoTotal),
      icon: <VideoLibraryIcon fontSize="large" color="primary" />,
      description: `Duração total no período`
    }
  ];

  return (
    <SidebarLayout>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Dashboard
        </Typography>
        <Typography variant="subtitle1" color="textSecondary">
          Bem-vindo(a) ao painel de controle do CCPMIDIA.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {statsCards.map((card) => (
          <Grid item xs={12} sm={6} md={4} key={card.title}>
            <Card 
              elevation={2}
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
                }
              }}
            >
              <CardHeader
                avatar={card.icon}
                title={card.title}
                titleTypographyProps={{ variant: 'h6' }}
              />
              <Divider />
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="h3" component="div" gutterBottom>
                  {card.value}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {card.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 4 }}>
        <Paper elevation={2} sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="h5" gutterBottom sx={{ mb: 0, mr: 2 }}>
                Dashboard
              </Typography>
              <ButtonGroup variant="outlined" size="small" aria-label="date range selector">
                <Button 
                  onClick={() => setDateRange(7)} 
                  variant={dateRange === 7 ? 'contained' : 'outlined'}
                >
                  7 dias
                </Button>
                <Button 
                  onClick={() => setDateRange(14)} 
                  variant={dateRange === 14 ? 'contained' : 'outlined'}
                >
                  14 dias
                </Button>
                <Button 
                  onClick={() => setDateRange(30)} 
                  variant={dateRange === 30 ? 'contained' : 'outlined'}
                >
                  30 dias
                </Button>
              </ButtonGroup>
            </Box>
            {isLoading ? (
              <CircularProgress size={24} />
            ) : (
              <Chip 
                label={`Total: ${registroStats.total} registros`} 
                color="primary" 
                variant="outlined" 
                icon={<DateRangeIcon />}
              />
            )}
          </Box>
          <Divider sx={{ mb: 2 }} />
          
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : registros.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="textSecondary">
                Nenhum registro encontrado para o período selecionado.
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0}>
              <Table size="small" aria-label="daily records table">
                <TableHead>
                  <TableRow>
                    <StyledTableCell>Horário</StyledTableCell>
                    <StyledTableCell>Emissora</StyledTableCell>
                    <StyledTableCell>Marca</StyledTableCell>
                    <StyledTableCell>Título</StyledTableCell>
                    <StyledTableCell>Duração</StyledTableCell>
                    <StyledTableCell>Tipo</StyledTableCell>
                    <StyledTableCell align="right">Custo</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {registros.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((registro) => (
                    <StyledTableRow key={registro.id}>
                      <TableCell>
                        {registro.datahora && isValid(parseISO(registro.datahora)) ? 
                          format(parseISO(registro.datahora), 'dd/MM/yyyy HH:mm', { locale: ptBR }) : 
                          'N/A'}
                      </TableCell>
                      <TableCell>{emissorasDisponiveis.find(e => e.id === registro.emissora_id)?.nome || registro.emissora?.nome || 'N/A'}</TableCell>
                      <TableCell>{marcasDisponiveis.find(m => m.id === registro.marca_id)?.nome || registro.marca?.nome || 'N/A'}</TableCell>
                      <TableCell>
                        <Tooltip title={registro.titulo || 'Sem título'}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                            {registro.titulo || 'Sem título'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{formatDuration(registro.duracao || 0)}</TableCell>
                      <TableCell>
                        <Chip 
                          label={registro.tipo || 'N/A'} 
                          size="small" 
                          variant="outlined"
                          color={getChipColor(registro.tipo || '')}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {registro.custo ? 
                          `R$ ${registro.custo.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : 
                          'N/A'}
                      </TableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[15]}
                component="div"
                count={registros.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(event, newPage) => setPage(newPage)}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                labelRowsPerPage="Registros por página:"
              />
            </TableContainer>
          )}
        </Paper>
      </Box>
      
      {/* Daily Volume Chart */}
      <Box sx={{ mt: 4 }}>
        <Paper elevation={2} sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
              Volumetria de Registros Diários
            </Typography>
            <Chip 
              label={`Últimos ${dateRange} dias`} 
              color="primary" 
              variant="outlined" 
              icon={<BarChartIcon />}
            />
          </Box>
          <Divider sx={{ mb: 3 }} />
          
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
              <CircularProgress />
            </Box>
          ) : registroStats.dailyData.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="textSecondary">
                Nenhum dado disponível para visualização.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ height: 300, mt: 2 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={registroStats.dailyData}
                  margin={{ top: 5, right: 30, left: 20, bottom: 25 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="formattedDate" 
                    height={60} 
                    tick={{ fontSize: 12 }}
                    label={{ 
                      value: 'Data', 
                      position: 'insideBottomRight', 
                      offset: -15 
                    }}
                  />
                  <YAxis 
                    label={{ 
                      value: 'Quantidade de Registros', 
                      angle: -90, 
                      position: 'insideLeft',
                      style: { textAnchor: 'middle' }
                    }}
                  />
                  <RechartsTooltip 
                    formatter={(value: number) => [`${value} registro${value !== 1 ? 's' : ''}`, 'Quantidade']}
                    labelFormatter={(label: string) => `Data: ${label}`}
                  />
                  <Legend verticalAlign="top" height={36} />
                  <Bar 
                    dataKey="count" 
                    name="Registros" 
                    fill="#82ca9d"
                    radius={[4, 4, 0, 0]}
                  >
                    <LabelList dataKey="count" position="top" style={{ fontSize: '11px' }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          )}
        </Paper>
      </Box>
      
      {/* Time Distribution Chart */}
      <Box sx={{ mt: 4 }}>
        <Paper elevation={2} sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
              Distribuição de Registros por Horário
            </Typography>
            <Chip 
              label={`Últimos ${dateRange} dias`} 
              color="primary" 
              variant="outlined" 
              icon={<AccessTimeIcon />}
            />
          </Box>
          <Divider sx={{ mb: 3 }} />
          
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
              <CircularProgress />
            </Box>
          ) : registroStats.hourlyData.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="textSecondary">
                Nenhum dado disponível para visualização.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ height: 300, mt: 2 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={registroStats.hourlyData}
                  margin={{ top: 5, right: 30, left: 20, bottom: 25 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="hour" 
                    height={60} 
                    tick={{ fontSize: 12 }} 
                    label={{ 
                      value: 'Horário do Dia', 
                      position: 'insideBottomRight', 
                      offset: -15 
                    }}
                  />
                  <YAxis 
                    label={{ 
                      value: 'Quantidade de Registros', 
                      angle: -90, 
                      position: 'insideLeft',
                      style: { textAnchor: 'middle' }
                    }} 
                  />
                  <RechartsTooltip 
                    formatter={(value: number, name: string) => [
                      `${value} registro${value !== 1 ? 's' : ''}`, 
                      'Quantidade'
                    ]}
                    labelFormatter={(label: string) => `Horário: ${label}`}
                  />
                  <Legend verticalAlign="top" height={36} />
                  <Line 
                    type="monotone" 
                    dataKey="count" 
                    name="Registros" 
                    stroke="#8884d8" 
                    activeDot={{ r: 8 }} 
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          )}
        </Paper>
      </Box>
    </SidebarLayout>
  );
};

// Format duration from seconds to mm:ss
const formatDuration = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

// Get color for chip based on registro type
const getChipColor = (tipo: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  const typeMap: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
    'Comercial': 'primary',
    'Publicidade': 'secondary',
    'Chamada': 'info',
    'Patrocínio': 'success',
    'Merchandising': 'warning'
  };
  
  return typeMap[tipo] || 'default';
};

export default Dashboard;
